import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ListCard from "../components/ListCard";
import ItemDialog from "../components/ItemDialog";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import { removeItem, upsertItem } from "../../hooks/useTrustCenterAdmin";
import { includesQuery } from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  UPDATE_STATUSES,
  formatDate,
  usedCategories,
} from "../../data/updates";
import usePresentFilter from "../../hooks/usePresentFilter";

const columns = [
  { label: "Update", field: "title", width: "46%" },
  // Stored as `category`; admins see it as a tag they type themselves.
  { label: "Tag", field: "category", width: "14%" },
  { label: "Status", field: "status", width: "14%" },
  { label: "Published", field: "publishedAt", width: "14%" },
  { label: "Actions", field: "actions", width: "12%", align: "center" },
];

// Status chips, then one chip per tag the admin has used.
const filtersFor = (categories) => ({
  Published: (u) => u.status === "published",
  Drafts: (u) => u.status === "draft",
  ...Object.fromEntries(
    categories.map((category) => [category, (u) => u.category === category])
  ),
});

const EMPTY_UPDATE = {
  title: "",
  category: "",
  body: "",
  status: "published",
};

const fieldsFor = (categories) => [
  { name: "title", label: "Title", type: "text", required: true },
  {
    name: "category",
    label: "Tag (optional)",
    type: "suggest",
    options: categories,
    maxLength: 40,
    helperText:
      "Type your own tag, like Compliance or Product, or pick one you used before. Leave it empty for no tag.",
  },
  {
    name: "body",
    label: "Post",
    type: "textarea",
    minRows: 6,
    required: true,
    helperText: "Plain words. The first two lines show before Read more.",
  },
  {
    name: "status",
    label: "Status",
    type: "select",
    options: [
      { value: "published", label: "Published (visible on your Trust Center)" },
      { value: "draft", label: "Draft (only you can see it)" },
    ],
  },
];

// Updates tab: posts shown on the public Updates tab, newest first.
const UpdatesTab = ({ updates, subscriberCount, onChange, onNotify }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const categories = useMemo(() => usedCategories(updates), [updates]);
  const filters = useMemo(() => filtersFor(categories), [categories]);
  const fields = useMemo(() => fieldsFor(categories), [categories]);
  const {
    filter,
    setFilter,
    options: filterOptions,
  } = usePresentFilter(updates, filters);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  // The update waiting for "Remove?" to be confirmed (row button).

  const rows = useMemo(
    () =>
      [...updates]
        .filter(
          (u) =>
            (!filter || filters[filter]?.(u)) &&
            includesQuery(search, u.title, u.body, u.category)
        )
        // Drafts first, then newest published.
        .sort(
          (a, b) =>
            new Date(b.publishedAt || 8.64e15) -
            new Date(a.publishedAt || 8.64e15)
        ),
    [updates, search, filter, filters]
  );

  const save = (values) => {
    const previous = updates.find((u) => u.id === values.id);
    const publishingNow =
      values.status === "published" && previous?.status !== "published";
    // TODO: the API will email subscribers when a post is first published.
    Promise.resolve(
      onChange({
        updates: upsertItem(updates, {
          ...values,
          title: values.title.trim(),
          body: values.body.trim(),
          // Keep the first publish date; drafts have none.
          publishedAt:
            values.status === "published"
              ? previous?.publishedAt || new Date().toISOString()
              : null,
        }),
      })
    ).then((ok) => ok && publishingNow && onNotify("Update published."));
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "title":
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.customColors.text.secondary,
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.body}
            </Typography>
          </Box>
        );
      case "category":
        return row.category ? (
          <Chip
            size="small"
            label={row.category}
            sx={{ backgroundColor: theme.customColors.background.light }}
          />
        ) : null;
      case "status":
        return (
          <Chip
            size="small"
            label={UPDATE_STATUSES[row.status]}
            sx={
              row.status === "published"
                ? {
                    color: theme.customColors.status.success.text,
                    backgroundColor: theme.customColors.status.success.bg,
                  }
                : { backgroundColor: theme.customColors.background.light }
            }
          />
        );
      case "publishedAt":
        return formatDate(row.publishedAt) || "—";
      case "actions":
        return (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip title="Remove" arrow>
              <IconButton
                size="small"
                aria-label={`Remove ${row.title}`}
                onClick={() => setRemoving(row)}
                sx={{ color: theme.customColors.status.error.text }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit update" arrow>
              <IconButton
                size="small"
                aria-label={`Edit ${row.title}`}
                onClick={() => setEditing(row)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      default:
        return row[column.field];
    }
  };

  return (
    <>
      <ListCard
        title="Updates"
        subtitle={`Share news about your security and compliance, like a new report or a new subprocessor. ${subscriberCount} ${subscriberCount === 1 ? "person has" : "people have"} subscribed to updates.`}
        addLabel="New update"
        onAdd={() => setEditing(EMPTY_UPDATE)}
        searchQuery={search}
        onSearchChange={setSearch}
        filters={filterOptions}
        selectedFilter={filter}
        onFilterChange={setFilter}
        onClearFilters={() => {
          setSearch("");
          setFilter(null);
        }}
        countNoun="update"
        columns={columns}
        rows={rows}
        renderCell={renderCell}
        emptyText="No updates yet. Post one when you get a new report or certification."
      />
      <ItemDialog
        open={!!editing}
        title={editing?.id ? "Edit update" : "New update"}
        fields={fields}
        initialValues={editing}
        emptyValues={EMPTY_UPDATE}
        onClose={() => setEditing(null)}
        onSave={save}
        onRemove={(id) => onChange({ updates: removeItem(updates, id) })}
        removeHelperText="It will no longer appear on your Trust Center."
      />
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          onChange({ updates: removeItem(updates, removing.id) });
          setRemoving(null);
        }}
        heading={`Remove ${removing?.title || "this update"}?`}
        content="It will no longer appear on your Trust Center."
        buttonText="Remove"
      />
    </>
  );
};

export default UpdatesTab;
